import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getTurnos,
  getTiposTurno,
  getEmpleadosHorarios,
  getSedesHorarios,
  getNovedades,
  crearTurno,
  crearNovedad,
  eliminarTurnoDia, 
  generarMesAutomatico,
  getConfiguracionGerencialApi
} from '../services/horariosApi';
import { resolverAlcanceABAC } from '../utils/abacHorarios';

const DIAS_NOMBRES = ['DOMINGO', 'LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO'];
const DIAS_CABECERA_MES = ['LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO', 'DOMINGO'];
const MESES_NOMBRES = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];
const MESES_CAPITAL = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const formatearHora12 = (horaStr) => {
  if (!horaStr) return '';
  const [hh, mm] = String(horaStr).split(':').map(Number);
  if (isNaN(hh)) return horaStr;
  const sufijo = hh >= 12 ? 'p. m.' : 'a. m.';
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${h12}:${String(mm || 0).padStart(2, '0')} ${sufijo}`;
};

const formatearHorasTotales = (numHoras) => {
  const val = Number(numHoras || 0);
  const enteras = Math.floor(val);
  const minutos = Math.round((val - enteras) * 60);
  return `${String(enteras).padStart(2, '0')}:${String(minutos).padStart(2, '0')}`;
};

const obtenerLunesDeSemana = (fechaBase) => {
  const d = new Date(fechaBase);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
};

export default function CalendarioTurnos({ usuario, onVolver }) {
  const [modoVista, setModoVista] = useState('SEMANAL');
  const [lunesActual, setLunesActual] = useState(() => obtenerLunesDeSemana(new Date()));
  const [mesActualDate, setMesActualDate] = useState(() => {
    const hoy = new Date();
    return new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  });

  const [sedeSeleccionada, setSedeSeleccionada] = useState('');
  const [cargoFiltro, setCargoFiltro] = useState('');
  const [sedes, setSedes] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [turnos, setTurnos] = useState([]);
  const [tiposTurno, setTiposTurno] = useState([]);
  const [novedades, setNovedades] = useState([]);
  const [configGlobal, setConfigGlobal] = useState({});
  const [cargando, setCargando] = useState(true);
  const [generando, setGenerando] = useState(false);
  
  const [alertasOcultas, setAlertasOcultas] = useState(false);

  // Modales
  const [celdaEditando, setCeldaEditando] = useState(null);
  const [esCoberturaDoblaje, setEsCoberturaDoblaje] = useState(false);
  const [diaMesEditando, setDiaMesEditando] = useState(null);
  const [modalExtraAbierto, setModalExtraAbierto] = useState(false);
  const [guardandoExtra, setGuardandoExtra] = useState(false);
  const [toast, setToast] = useState(null);

  const hoyStr = useMemo(() => new Date().toISOString().split('T')[0], []);
  const ayerStr = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().split('T')[0];
  }, []);

  const [formExtra, setFormExtra] = useState({ id_empleado: '', fecha_inicio: hoyStr, horas_extra: 2, motivo: '' });

  const mostrarToast = useCallback((mensaje, tipo = 'success') => {
    setToast({ mensaje, tipo });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const cargarDatos = useCallback(async () => {
    setCargando(true);
    try {
      const [resSedes, resEmp, resTurnos, resTipos, resNov, resConf] = await Promise.all([
        getSedesHorarios().catch(() => ({ success: false, data: [] })),
        getEmpleadosHorarios().catch(() => ({ success: false, data: [] })),
        getTurnos().catch(() => ({ success: false, data: [] })),
        getTiposTurno().catch(() => ({ success: false, data: [] })),
        getNovedades().catch(() => ({ success: false, data: [] })),
        getConfiguracionGerencialApi().catch(() => ({ success: false, data: {} }))
      ]);

      if (resSedes?.success) setSedes(resSedes.data || []);
      if (resEmp?.success) setEmpleados(resEmp.data || []);
      if (resTurnos?.success) setTurnos(resTurnos.data || []);
      if (resTipos?.success) setTiposTurno(resTipos.data || []);
      if (resNov?.success) setNovedades(resNov.data || []);
      if (resConf?.success) setConfigGlobal(resConf.data || {});
    } catch (err) {
      mostrarToast('Error cargando programación: ' + err.message, 'error');
    } finally {
      setCargando(false);
    }
  }, [mostrarToast]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  const alcanceABAC = useMemo(() => resolverAlcanceABAC(usuario, sedes), [usuario, sedes]);

  useEffect(() => {
    if (alcanceABAC.sedesPermitidas.length > 0 && !sedeSeleccionada) {
      setSedeSeleccionada(String(alcanceABAC.sedesPermitidas[0].prefijo || ''));
    }
  }, [alcanceABAC, sedeSeleccionada]);

  const empleadosSede = useMemo(() => {
    return empleados
      .filter(e => {
        if (String(e.estado || 'ACTIVO').toUpperCase() !== 'ACTIVO' || !e.codigo_interno) return false;
        if (sedeSeleccionada && !String(e.codigo_interno).toUpperCase().startsWith(sedeSeleccionada.toUpperCase())) return false;
        if (cargoFiltro && (e.cargo_nombre || e.cargo_original) !== cargoFiltro) return false;
        return true;
      })
      .sort((a, b) => {
        const pA = String(a.codigo_interno).split('-')[1];
        const pB = String(b.codigo_interno).split('-')[1];
        const nivelA = (a.tipo_rol_wfm === 'PRIMARIO' || pA === 'ADM' || pA === 'SUB') ? 1 : (pA === 'CFR' || pA === 'CRE' || pA === 'CAD' || pA === 'ACP') ? 2 : 3;
        const nivelB = (b.tipo_rol_wfm === 'PRIMARIO' || pB === 'ADM' || pB === 'SUB') ? 1 : (pB === 'CFR' || pB === 'CRE' || pB === 'CAD' || pB === 'ACP') ? 2 : 3;
        if (nivelA !== nivelB) return nivelA - nivelB;
        return a.nombre_completo.localeCompare(b.nombre_completo);
      });
  }, [empleados, sedeSeleccionada, cargoFiltro]);

  const cargosDisponiblesSede = useMemo(() => {
    const list = empleadosSede.map(e => e.cargo_nombre || e.cargo_original || 'Sin Cargo');
    return Array.from(new Set(list)).sort();
  }, [empleadosSede]);

  const sedeTieneExtrasActivas = useMemo(() => {
    if (!sedeSeleccionada) return true;
    return configGlobal.sedesExtrasHabilitadas?.[sedeSeleccionada.toUpperCase()] !== false;
  }, [sedeSeleccionada, configGlobal]);

  const diasSemana = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(lunesActual); d.setDate(lunesActual.getDate() + i);
      arr.push({ fechaStr: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`, diaNumero: d.getDate(), diaSemana: d.getDay(), nombreDia: DIAS_NOMBRES[d.getDay()] });
    }
    return arr;
  }, [lunesActual]);

  const infoMesActivo = useMemo(() => {
    if (modoVista === 'MENSUAL') {
      const anio = mesActualDate.getFullYear(); const mesIdx = mesActualDate.getMonth();
      return { anio, mesNumero: mesIdx + 1, nombreMesMayus: MESES_NOMBRES[mesIdx], nombreMesCap: MESES_CAPITAL[mesIdx] };
    }
    const baseStr = diasSemana[3]?.fechaStr || hoyStr;
    const [anio, mes] = baseStr.split('-').map(Number);
    return { anio, mesNumero: mes, nombreMesMayus: MESES_NOMBRES[(mes || 1) - 1], nombreMesCap: MESES_CAPITAL[(mes || 1) - 1] };
  }, [modoVista, mesActualDate, diasSemana, hoyStr]);

  const tituloSemana = useMemo(() => {
    if (diasSemana.length < 7) return '';
    const ini = diasSemana[0]; const fin = diasSemana[6];
    const mesIndex = Number(fin.fechaStr.split('-')[1]) - 1;
    return `SEMANA DEL ${ini.diaNumero} AL ${fin.diaNumero} DE ${MESES_NOMBRES[mesIndex]} ${fin.fechaStr.split('-')[0]}`;
  }, [diasSemana]);

  const mapaTurnos = useMemo(() => {
    const map = new Map();
    turnos.forEach(t => map.set(`${t.id_empleado}_${String(t.fecha).split('T')[0]}`, t));
    return map;
  }, [turnos]);

  const { mapaAusencias, mapaExtras } = useMemo(() => {
    const ausencias = new Map(); const extras = new Map();
    novedades.forEach(n => {
      const esExtra = String(n.tipo_novedad || '').toUpperCase().includes('EXTRA') || Number(n.horas_extra || 0) > 0;
      const estado = String(n.estado_aprobacion || 'APROBADA').toUpperCase();
      if (estado === 'RECHAZADA') return;
      const fIni = String(n.fecha_inicio || '').split('T')[0];
      const fFin = String(n.fecha_fin || fIni).split('T')[0];
      if (!fIni) return;
      const cur = new Date(`${fIni}T00:00:00`); const end = new Date(`${fFin}T00:00:00`);
      while (cur <= end) {
        const fKey = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
        const key = `${n.id_empleado}_${fKey}`;
        if (esExtra) {
          const prev = extras.get(key) || { aprobadas: 0, pendientes: 0 };
          if (estado === 'APROBADA') prev.aprobadas += Number(n.horas_extra || 0); else prev.pendientes += Number(n.horas_extra || 0);
          extras.set(key, prev);
        } else ausencias.set(key, n);
        cur.setDate(cur.getDate() + 1);
      }
    });
    return { mapaAusencias: ausencias, mapaExtras: extras };
  }, [novedades]);

  const alertasOperativas = useMemo(() => {
    const alertas = [];
    if (!sedeSeleccionada) return alertas;

    diasSemana.forEach(d => {
      let jefeTrabaja = false;
      empleadosSede.forEach(emp => {
        const pref = String(emp.codigo_interno).split('-')[1];
        // WFM FIX: Alerta dinámica basada en la BD (tipo_rol_wfm === 'PRIMARIO')
        const esPrimario = emp.tipo_rol_wfm === 'PRIMARIO' || ['ADM', 'SUB'].includes(pref);
        
        if (!esPrimario) return;
        const key = `${emp.id}_${d.fechaStr}`;
        const turno = mapaTurnos.get(key); const ausencia = mapaAusencias.get(key);
        if (!ausencia && turno) {
          jefeTrabaja = true;
        }
      });
      if (!jefeTrabaja && empleadosSede.length > 0) {
        alertas.push(`🚨 Riesgo Crítico el ${d.nombreDia}: La sede no tiene personal del Grupo Primario (Administración) programado.`);
      }
    });
    return alertas;
  }, [diasSemana, empleadosSede, mapaTurnos, mapaAusencias, sedeSeleccionada]);

  const celdasGrillaMensual = useMemo(() => {
    const anio = mesActualDate.getFullYear(); const mesIdx = mesActualDate.getMonth();
    const totalDias = new Date(anio, mesIdx + 1, 0).getDate();
    const offsetLunes = (new Date(anio, mesIdx, 1).getDay() + 6) % 7;
    const celdas = [];
    for (let i = 0; i < offsetLunes; i++) celdas.push({ vacia: true, key: `vacia_${i}` });
    for (let dia = 1; dia <= totalDias; dia++) {
      const fechaStr = `${anio}-${String(mesIdx + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
      const turnosDia = []; let descansosCount = 0; let ausenciasCount = 0;
      empleadosSede.forEach(emp => {
        const key = `${emp.id}_${fechaStr}`;
        const aus = mapaAusencias.get(key); const tur = mapaTurnos.get(key); const ext = mapaExtras.get(key);
        if (aus) ausenciasCount++;
        else if (tur) turnosDia.push({ emp, turno: tur, horaCorta: formatearHora12(tur.hora_inicio_1), esPartido: Boolean(tur.hora_inicio_2 && tur.hora_fin_2), extrasAprobadas: Number(tur.horas_extra_aprobadas || ext?.aprobadas || 0) });
        else descansosCount++;
      });
      celdas.push({ vacia: false, key: fechaStr, dia, fechaStr, diaSemana: new Date(anio, mesIdx, dia).getDay(), activosCount: turnosDia.length, descansosCount, ausenciasCount, turnosDia });
    }
    return celdas;
  }, [mesActualDate, empleadosSede, mapaTurnos, mapaAusencias, mapaExtras]);

  const cambiarSemana = (deltaDias) => { const nuevo = new Date(lunesActual); nuevo.setDate(nuevo.getDate() + deltaDias); setLunesActual(nuevo); setMesActualDate(new Date(nuevo.getFullYear(), nuevo.getMonth(), 1)); };
  const cambiarMes = (deltaMeses) => { const nuevo = new Date(mesActualDate.getFullYear(), mesActualDate.getMonth() + deltaMeses, 1); setMesActualDate(nuevo); setLunesActual(obtenerLunesDeSemana(nuevo)); };

  const handleGenerarMes = async () => {
    const { anio, mesNumero, nombreMesCap } = infoMesActivo;
    setGenerando(true);
    try {
      const res = await generarMesAutomatico(anio, mesNumero, sedeSeleccionada);
      mostrarToast(res.message || `Malla de ${nombreMesCap} generada exitosamente`);
      cargarDatos();
    } catch (err) { mostrarToast(err.message, 'error'); } finally { setGenerando(false); }
  };

  const handleAsignarTurnoManual = async (idTurno) => {
    if (!celdaEditando) return;
    try {
      await crearTurno(celdaEditando.id_empleado, celdaEditando.fechaStr, Number(idTurno), esCoberturaDoblaje ? 1 : 0);
      mostrarToast(esCoberturaDoblaje ? 'Cobertura registrada correctamente' : 'Turno actualizado en la sábana');
      setCeldaEditando(null); setEsCoberturaDoblaje(false); cargarDatos();
    } catch (err) { mostrarToast(err.message, 'error'); }
  };

  const handleAsignarDescanso = async () => {
    if (!celdaEditando) return;
    try {
      if (typeof eliminarTurnoDia === 'function') {
        await eliminarTurnoDia(celdaEditando.id_empleado, celdaEditando.fechaStr);
      } else {
        mostrarToast('⚠️ Necesitas agregar el endpoint eliminarTurnoDia en tu API', 'error');
        return;
      }
      mostrarToast('Día marcado como descanso (Turno liberado).');
      setCeldaEditando(null); 
      setEsCoberturaDoblaje(false); 
      cargarDatos();
    } catch (err) {
      mostrarToast('Error al asignar descanso: ' + err.message, 'error');
    }
  };

  const handleGuardarHoraExtra = async (e) => {
    e.preventDefault();
    if (!formExtra.id_empleado) return mostrarToast('Selecciona un colaborador', 'error');
    setGuardandoExtra(true);
    try {
      const res = await crearNovedad({ id_empleado: Number(formExtra.id_empleado), tipo_novedad: 'HORA_EXTRA', fecha_inicio: formExtra.fecha_inicio, fecha_fin: formExtra.fecha_inicio, horas_extra: Number(formExtra.horas_extra || 1), motivo: formExtra.motivo.trim(), solicitado_por: usuario?.nombres || usuario?.nombre || 'Administrador de Punto', es_gerencia: alcanceABAC.esGlobal });
      mostrarToast(res.message || 'Solicitud enviada a Gerencia'); setModalExtraAbierto(false); setFormExtra({ id_empleado: '', fecha_inicio: hoyStr, horas_extra: 2, motivo: '' }); cargarDatos();
    } catch (err) { mostrarToast(err.message, 'error'); } finally { setGuardandoExtra(false); }
  };

  return (
    <div className="animate-fade-in space-y-5 relative pb-10">
      {toast && (
        <div className={`fixed top-6 right-6 z-50 px-5 py-3.5 rounded-2xl shadow-xl font-black text-xs text-white ${toast.tipo === 'error' ? 'bg-rose-600' : 'bg-slate-900'}`}>
          {toast.mensaje}
        </div>
      )}

      {alertasOperativas.length > 0 && modoVista === 'SEMANAL' && !alertasOcultas && (
        <div className="bg-red-50/90 border border-red-200 rounded-2xl p-4 shadow-sm animate-fade-in">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-black text-red-800 text-xs uppercase flex items-center gap-1.5">
              <span className="animate-pulse">⚠️</span> 
              {alertasOperativas.length} Alertas de Riesgo Operativo (Regla ADM/SUB)
            </h4>
            <button onClick={() => setAlertasOcultas(true)} className="text-red-500 font-bold text-[10px] hover:text-red-700 hover:underline cursor-pointer">
              Ocultar ✕
            </button>
          </div>
          <ul className="space-y-1 max-h-24 overflow-y-auto custom-scrollbar pr-2">
            {alertasOperativas.map((alerta, i) => (
              <li key={i} className="text-red-700 text-[11px] font-bold border-b border-red-100/50 pb-1">{alerta}</li>
            ))}
          </ul>
        </div>
      )}
      
      {alertasOcultas && alertasOperativas.length > 0 && modoVista === 'SEMANAL' && (
        <button onClick={() => setAlertasOcultas(false)} className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-[10px] font-black hover:bg-red-100 border border-red-100 transition cursor-pointer">
          ⚠️ Mostrar {alertasOperativas.length} Alertas Ocultas
        </button>
      )}

      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 border-b border-gray-200 pb-5">
        <div>
          <div className="flex flex-wrap items-center gap-2.5">
            <h2 className="text-2xl font-black text-slate-800 flex items-center gap-2"><span>📅</span> Programación y Sábana</h2>
            <span className="px-3 py-1 rounded-full bg-white/90 text-blue-700 font-black text-[10px] border border-blue-200 shadow-xs">
              🔓 ABAC: {alcanceABAC.esGlobal ? 'Todas las Sedes' : 'Sede Asignada'}
            </span>
          </div>
          <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mt-1">
            Gestión de turnos, coberturas dinámicas y equilibrio de jerarquías
          </p>
        </div>

        <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5 w-full xl:w-auto">
          <div className="bg-white/80 p-1 rounded-2xl border border-gray-200 flex items-center gap-1 shadow-xs">
            <button onClick={() => setModoVista('SEMANAL')} className={`px-3.5 py-2 rounded-xl text-xs font-black transition cursor-pointer flex items-center gap-1.5 ${modoVista === 'SEMANAL' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-700 hover:bg-gray-100'}`}>📊 Sábana Semanal</button>
            <button onClick={() => setModoVista('MENSUAL')} className={`px-3.5 py-2 rounded-xl text-xs font-black transition cursor-pointer flex items-center gap-1.5 ${modoVista === 'MENSUAL' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-700 hover:bg-gray-100'}`}>🗓️ Grilla Mensual</button>
          </div>

          <div className="flex gap-2 w-full sm:w-auto">
            <select value={sedeSeleccionada} onChange={(e) => { setSedeSeleccionada(e.target.value); setCargoFiltro(''); }} className="w-1/2 sm:w-auto px-4 py-2.5 rounded-2xl bg-white border border-gray-200 text-xs font-black text-slate-800 shadow-xs cursor-pointer">
              {alcanceABAC.esGlobal && <option value="">Todas las sedes</option>}
              {alcanceABAC.sedesPermitidas.map(s => <option key={s.id} value={s.prefijo}>{s.nombre} ({s.prefijo})</option>)}
            </select>
            <select value={cargoFiltro} onChange={(e) => setCargoFiltro(e.target.value)} className="w-1/2 sm:w-auto px-4 py-2.5 rounded-2xl bg-slate-50 border border-slate-200 text-xs font-bold text-slate-700 shadow-xs cursor-pointer">
              <option value="">Todos los Cargos</option>
              {cargosDisponiblesSede.map((c, i) => <option key={i} value={c}>{c}</option>)}
            </select>
          </div>

          <button onClick={() => {
            if (!sedeTieneExtrasActivas && !alcanceABAC.esGlobal) { mostrarToast('Extras bloqueadas para esta sede.', 'error'); return; }
            setModalExtraAbierto(true);
          }} className={`px-4 py-2.5 rounded-2xl font-black text-xs shadow-md transition cursor-pointer flex items-center justify-center gap-1.5 ${sedeTieneExtrasActivas || alcanceABAC.esGlobal ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
            {sedeTieneExtrasActivas || alcanceABAC.esGlobal ? '⏱️ Solicitar Extra' : '🔒 Extras Bloqueadas'}
          </button>
          <button onClick={handleGenerarMes} disabled={generando} className="px-5 py-2.5 rounded-2xl bg-purple-600 hover:bg-purple-700 text-white font-black text-xs shadow-md transition cursor-pointer">
            {generando ? 'Generando...' : `✨ Generar Mes`}
          </button>
          <button onClick={onVolver} className="px-5 py-2.5 rounded-2xl bg-white border border-gray-200 text-slate-700 font-bold text-xs hover:bg-gray-50 transition cursor-pointer">Volver</button>
        </div>
      </div>

      {modoVista === 'SEMANAL' && (
        <>
          <div className="bg-white/90 p-4 rounded-2xl border border-gray-200 shadow-sm flex items-center justify-between">
            <button onClick={() => cambiarSemana(-7)} className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-slate-700 font-black text-xs cursor-pointer">◀ Anterior</button>
            <div className="text-center"><h3 className="text-base sm:text-lg font-black text-slate-900">{tituloSemana}</h3></div>
            <button onClick={() => cambiarSemana(7)} className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-slate-700 font-black text-xs cursor-pointer">Siguiente ▶</button>
          </div>

          <div className="bg-white rounded-3xl border border-gray-300 shadow-lg overflow-x-auto max-h-[72vh] custom-scrollbar">
            <table className="w-full border-collapse text-[11px]">
              <thead className="sticky top-0 z-20 bg-[#2b548e] text-white uppercase font-black">
                <tr>
                  <th rowSpan="3" className="p-3 text-left min-w-[260px] border border-blue-900/40 sticky left-0 z-30 bg-[#2b548e]">
                    <div>COLABORADOR / CARGO</div><div className="text-[9px] font-bold text-blue-200">ID OPERATIVO • DESCANSO FIJO</div>
                  </th>
                  {diasSemana.map(d => <th key={d.fechaStr} colSpan="5" className="p-2 text-center border border-blue-900/40 tracking-wider">{d.nombreDia} {d.diaNumero}</th>)}
                  <th rowSpan="3" className="p-2 text-center border border-blue-900/40 bg-[#1e3a63] min-w-[90px]">TOTAL SEMANAL</th>
                  <th rowSpan="3" className="p-2 text-center border border-blue-900/40 bg-slate-900 min-w-[105px]">TOPE CARGO / EXTRAS</th>
                </tr>
                <tr className="bg-[#3b69a9] text-[10px]">
                  {diasSemana.map(d => <React.Fragment key={`sub_${d.fechaStr}`}><th colSpan="2" className="p-1.5 border border-blue-800/40">Mañana</th><th colSpan="2" className="p-1.5 border border-blue-800/40">Tarde</th><th rowSpan="2" className="p-1.5 border border-blue-800/40 bg-[#23477a] min-w-[54px]">Total</th></React.Fragment>)}
                </tr>
                <tr className="bg-[#c7d9f2] text-slate-800 text-[10px]">
                  {diasSemana.map(d => <React.Fragment key={`ini_${d.fechaStr}`}><th className="p-1 border border-slate-300">Ini</th><th className="p-1 border border-slate-300">Fin</th><th className="p-1 border border-slate-300">Ini</th><th className="p-1 border border-slate-300">Fin</th></React.Fragment>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {cargando ? <tr><td colSpan="38" className="p-12 text-center font-bold text-gray-500">Cargando sábana...</td></tr> : empleadosSede.length === 0 ? <tr><td colSpan="38" className="p-12 text-center font-bold text-gray-500">No hay colaboradores con este filtro.</td></tr> : empleadosSede.map(emp => {
                  let sumaSemanal = 0; let sumaExtrasAprobadasSem = 0; let sumaExtrasPendientesSem = 0;
                  const topeCargo = Number(emp.tope_horas_cargo || 42);
                  const prefCargo = String(emp.codigo_interno).split('-')[1];
                  
                  let bgFila = 'bg-white';
                  let bgLeft = 'bg-white';
                  
                  if (emp.tipo_rol_wfm === 'PRIMARIO' || prefCargo === 'ADM' || prefCargo === 'SUB') {
                    bgFila = 'bg-blue-50 hover:bg-blue-100'; bgLeft = 'bg-blue-50';
                  } else if (['CFR', 'CRE', 'CAD', 'ACP'].includes(prefCargo)) {
                    bgFila = 'bg-purple-50 hover:bg-purple-100'; bgLeft = 'bg-purple-50';
                  } else {
                    bgFila = 'bg-white hover:bg-gray-50'; bgLeft = 'bg-white';
                  }

                  return (
                    <tr key={emp.id} className={`transition ${bgFila}`}>
                      <td className={`p-2.5 border border-gray-300 sticky left-0 z-10 ${bgLeft} shadow-[3px_0_6px_rgba(0,0,0,0.06)] min-w-[260px]`}>
                        <div className="flex justify-between items-start gap-2">
                          <div>
                            <p className="font-black text-slate-900 leading-tight">{emp.nombre_completo}</p>
                            <p className="text-[10px] font-bold text-slate-600 uppercase mt-0.5">{emp.cargo_nombre || emp.cargo_original}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <span className="px-1.5 py-0.5 rounded bg-white text-slate-800 font-mono font-black text-[9px] border border-gray-300 block shadow-xs">{emp.codigo_interno}</span>
                            <span className="text-[9px] font-bold text-slate-600 block mt-0.5">
                              Desc: {emp.dia_descanso_fijo && emp.dia_descanso_fijo !== 'ROTATIVO' ? String(emp.dia_descanso_fijo).substring(0, 3).toUpperCase() : 'ROT'}
                            </span>
                          </div>
                        </div>
                      </td>

                      {diasSemana.map(d => {
                        const key = `${emp.id}_${d.fechaStr}`; const ausencia = mapaAusencias.get(key); const infoExtra = mapaExtras.get(key); const turno = mapaTurnos.get(key);
                        if (ausencia) return <React.Fragment key={key}><td colSpan="4" className="p-2 text-center bg-rose-100 text-rose-800 font-black border border-gray-300">🏥 {ausencia.tipo_novedad}</td><td className="p-2 text-center font-mono font-black bg-rose-200 text-rose-900 border border-gray-300">00:00</td></React.Fragment>;
                        
                        // WFM FIX: Si no hay turno asignado por la rotación mensual, es automáticamente Descanso
                        if (!turno) {
                          const extrasDiaSinTurno = Number(infoExtra?.aprobadas || 0);
                          sumaSemanal += extrasDiaSinTurno; sumaExtrasAprobadasSem += extrasDiaSinTurno; sumaExtrasPendientesSem += Number(infoExtra?.pendientes || 0);
                          return (
                            <React.Fragment key={key}>
                              <td colSpan="4" onClick={() => { setCeldaEditando({ id_empleado: emp.id, nombre: emp.nombre_completo, fechaStr: d.fechaStr }); setEsCoberturaDoblaje(false); }} className="p-2 text-center bg-[#ffe8d9] text-amber-900 font-black border border-gray-300 cursor-pointer hover:bg-[#ffdcc4]">
                                DESC {extrasDiaSinTurno > 0 && <span className="ml-2 px-1.5 py-0.5 bg-purple-600 text-white rounded text-[9px]">+{extrasDiaSinTurno}h Extra</span>}
                              </td>
                              <td className="p-2 text-center font-mono font-black bg-[#fff9cc] text-slate-900 border border-gray-300">{formatearHorasTotales(extrasDiaSinTurno)}</td>
                            </React.Fragment>
                          );
                        }

                        const esPartido = Boolean(turno.hora_inicio_2 && turno.hora_fin_2);
                        const horaInicioNumerico = Number(String(turno.hora_inicio_1 || '').split(':')[0]);
                        const esSoloTarde = !esPartido && horaInicioNumerico >= 12;

                        const t1i = esSoloTarde ? '' : formatearHora12(turno.hora_inicio_1);
                        const t1f = esSoloTarde ? '' : formatearHora12(turno.hora_fin_1);
                        const t2i = esPartido ? formatearHora12(turno.hora_inicio_2) : (esSoloTarde ? formatearHora12(turno.hora_inicio_1) : '');
                        const t2f = esPartido ? formatearHora12(turno.hora_fin_2) : (esSoloTarde ? formatearHora12(turno.hora_fin_1) : '');

                        const horasTotalesDia = Number(turno.horas_computables || 0);
                        const extrasAprobadas = Number(turno.horas_extra_aprobadas || infoExtra?.aprobadas || 0);
                        sumaSemanal += horasTotalesDia; sumaExtrasAprobadasSem += extrasAprobadas; sumaExtrasPendientesSem += Number(infoExtra?.pendientes || 0);
                        
                        const esCobertura = Boolean(Number(turno.es_cobertura));
                        const bgCeldaDia = esCobertura ? 'bg-sky-100' : esPartido ? 'bg-blue-50/50' : 'bg-transparent';

                        return (
                          <React.Fragment key={key}>
                            <td onClick={() => { setCeldaEditando({ id_empleado: emp.id, nombre: emp.nombre_completo, fechaStr: d.fechaStr }); setEsCoberturaDoblaje(esCobertura); }} className={`p-1.5 text-center font-bold ${esCobertura ? 'text-blue-900' : 'text-slate-800'} border border-gray-300 cursor-pointer ${bgCeldaDia}`}>{t1i}</td>
                            <td onClick={() => { setCeldaEditando({ id_empleado: emp.id, nombre: emp.nombre_completo, fechaStr: d.fechaStr }); setEsCoberturaDoblaje(esCobertura); }} className={`p-1.5 text-center font-bold ${esCobertura ? 'text-blue-900' : 'text-slate-800'} border border-gray-300 cursor-pointer ${bgCeldaDia}`}>{t1f}</td>
                            <td onClick={() => { setCeldaEditando({ id_empleado: emp.id, nombre: emp.nombre_completo, fechaStr: d.fechaStr }); setEsCoberturaDoblaje(esCobertura); }} className={`p-1.5 text-center font-bold ${esCobertura ? 'text-blue-900' : 'text-slate-800'} border border-gray-300 cursor-pointer ${bgCeldaDia}`}>{t2i}</td>
                            <td onClick={() => { setCeldaEditando({ id_empleado: emp.id, nombre: emp.nombre_completo, fechaStr: d.fechaStr }); setEsCoberturaDoblaje(esCobertura); }} className={`p-1.5 text-center font-bold ${esCobertura ? 'text-blue-900' : 'text-slate-800'} border border-gray-300 cursor-pointer ${bgCeldaDia}`}>
                              <div>{t2f}</div>
                              {extrasAprobadas > 0 && <span className="inline-block mt-0.5 px-1.5 py-0.2 bg-purple-600 text-white rounded font-black text-[9px]">+{extrasAprobadas}h Ext</span>}
                            </td>
                            <td className={`p-1.5 text-center font-mono font-black border border-gray-300 ${extrasAprobadas > 0 ? 'bg-purple-200 text-purple-950' : 'bg-[#fff9cc] text-slate-900'}`}>{formatearHorasTotales(horasTotalesDia)}</td>
                          </React.Fragment>
                        );
                      })}

                      <td className={`p-2 text-center font-mono font-black text-xs border border-gray-300 ${sumaExtrasAprobadasSem > 0 ? 'bg-purple-100 text-purple-900' : sumaSemanal >= topeCargo ? 'bg-emerald-100 text-emerald-900' : 'bg-amber-100 text-amber-900'}`}>{formatearHorasTotales(sumaSemanal)}</td>
                      <td className="p-2 text-center border border-gray-300 bg-slate-50">
                        <span className="block font-bold text-[10px] text-slate-600">Máx: {topeCargo}h</span>
                        {sumaExtrasAprobadasSem > 0 && <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-900 font-black text-[9px]">✓ +{sumaExtrasAprobadasSem}h Apr.</span>}
                        {sumaExtrasPendientesSem > 0 && <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 font-black text-[9px]">⏳ +{sumaExtrasPendientesSem}h Pend.</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {modoVista === 'MENSUAL' && (
        <>
          <div className="bg-white/90 p-4 rounded-2xl border border-gray-200 shadow-sm flex items-center justify-between">
            <button onClick={() => cambiarMes(-1)} className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-slate-700 font-black text-xs cursor-pointer">◀ Anterior</button>
            <div className="text-center"><h3 className="text-base sm:text-lg font-black text-slate-900">GRILLA MENSUAL — {infoMesActivo.nombreMesMayus} {infoMesActivo.anio}</h3></div>
            <button onClick={() => cambiarMes(1)} className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-slate-700 font-black text-xs cursor-pointer">Siguiente ▶</button>
          </div>
          <div className="bg-white/80 rounded-3xl p-4 sm:p-6 border border-gray-200 shadow-md">
            <div className="grid grid-cols-7 gap-3 mb-3">
              {DIAS_CABECERA_MES.map(diaNom => <div key={diaNom} className="bg-slate-900 text-white text-center py-2.5 rounded-xl font-black text-[11px] tracking-wider uppercase">{diaNom}</div>)}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
              {celdasGrillaMensual.map(celda => celda.vacia ? <div key={celda.key} className="hidden lg:block min-h-[210px] rounded-2xl border border-dashed border-gray-200 bg-gray-50/40" /> : (
                <div key={celda.key} className="bg-white rounded-2xl border border-gray-200 p-3 shadow-xs hover:shadow-md transition flex flex-col justify-between min-h-[220px]">
                  <div>
                    <div className="flex items-center justify-between gap-1 border-b border-gray-100 pb-2 mb-2">
                      <span className="w-7 h-7 rounded-full bg-blue-50 text-blue-700 font-black text-xs flex items-center justify-center border border-blue-200">{celda.dia}</span>
                      <div className="flex items-center gap-1">
                        <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 font-black text-[9px] border border-emerald-200">✓ {celda.activosCount}</span>
                        <span className="px-2 py-0.5 rounded-md bg-amber-50 text-amber-800 font-black text-[9px] border border-amber-200">Desc: {celda.descansosCount}</span>
                      </div>
                    </div>
                    <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1 custom-scrollbar">
                      {celda.turnosDia.map(item => {
                        const pref = String(item.emp.codigo_interno).split('-')[1];
                        const bgCol = (item.emp.tipo_rol_wfm === 'PRIMARIO' || pref === 'ADM' || pref === 'SUB') ? 'bg-blue-100 text-blue-900 border-blue-200' : 'bg-gray-50 border-gray-200 text-slate-800';
                        return (
                          <div key={`${celda.fechaStr}_${item.emp.id}`} onClick={() => { setCeldaEditando({ id_empleado: item.emp.id, nombre: item.emp.nombre_completo, fechaStr: celda.fechaStr }); setEsCoberturaDoblaje(false); }} className={`px-2 py-1 rounded-lg border text-[10px] flex items-center justify-between cursor-pointer transition ${item.esPartido ? 'bg-sky-50/90 border-sky-200 text-sky-900' : bgCol}`}>
                            <span className="font-mono font-black text-[9px]">{item.emp.codigo_interno}</span>
                            <span className="font-bold">{item.horaCorta}{item.extrasAprobadas > 0 ? ` (+${item.extrasAprobadas}h)` : ''}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <button onClick={() => setDiaMesEditando(celda)} className="mt-2.5 w-full py-1.5 rounded-xl bg-gray-50 hover:bg-blue-50 hover:text-blue-700 text-slate-600 font-black text-[10px] border border-gray-200 transition cursor-pointer">+ Asignar / Editar</button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {celdaEditando && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border border-gray-100 animate-fade-in">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
              <div>
                <h4 className="font-black text-slate-900 text-sm">{celdaEditando.nombre}</h4>
                <p className="text-xs text-gray-500 font-mono">Fecha: {celdaEditando.fechaStr}</p>
              </div>
              <button onClick={() => setCeldaEditando(null)} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 font-bold cursor-pointer">✕</button>
            </div>
            
            <button 
              onClick={handleAsignarDescanso} 
              className="w-full mb-4 p-3.5 rounded-xl bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 font-black text-xs text-center transition cursor-pointer flex items-center justify-center gap-2"
            >
              <span>🌴</span> Marcar como Descanso (Quitar Turno)
            </button>
            
            <div className="mb-4 bg-sky-50/70 p-3 rounded-xl border border-sky-100 flex items-center justify-between">
              <span className="text-[11px] font-bold text-sky-900">¿Asignar como turno de cobertura (reemplazo)?</span>
              <button 
                onClick={() => setEsCoberturaDoblaje(!esCoberturaDoblaje)}
                className={`w-11 h-6 rounded-full flex items-center transition-colors px-1 cursor-pointer ${esCoberturaDoblaje ? 'bg-sky-500 justify-end' : 'bg-gray-300 justify-start'}`}
              >
                <div className="w-4 h-4 bg-white rounded-full shadow-sm"></div>
              </button>
            </div>

            <p className="text-[10px] font-black uppercase text-gray-400 mb-2">Opciones de Turno</p>
            <div className="space-y-2 max-h-56 overflow-y-auto pr-1 custom-scrollbar">
              {tiposTurno.map(t => (
                <button key={t.id} onClick={() => handleAsignarTurnoManual(t.id)} className="w-full p-3 rounded-xl border border-gray-200 hover:border-blue-500 hover:bg-blue-50 text-left transition flex justify-between items-center cursor-pointer">
                  <span className="text-xs font-bold text-slate-800">{t.nombre_turno}</span>
                  <span className="px-2 py-0.5 rounded bg-slate-100 font-mono font-black text-[10px] text-slate-700">{t.horas_computables}h</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {modalExtraAbierto && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-gray-100 animate-fade-in">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
              <div><h3 className="text-base font-black text-slate-900">⏱️ Solicitar Hora Extra</h3></div>
              <button onClick={() => setModalExtraAbierto(false)} className="w-8 h-8 rounded-full bg-gray-100 font-bold cursor-pointer">✕</button>
            </div>
            <form onSubmit={handleGuardarHoraExtra} className="space-y-4">
              <div>
                <label className="block text-[10px] font-black text-gray-500 uppercase mb-1">Colaborador</label>
                <select required value={formExtra.id_empleado} onChange={(e) => setFormExtra({ ...formExtra, id_empleado: e.target.value })} className="w-full p-3 rounded-xl border border-gray-200 text-xs font-bold text-slate-800">
                  <option value="">Selecciona colaborador...</option>
                  {empleadosSede.map(emp => <option key={emp.id} value={emp.id}>{emp.codigo_interno} — {emp.nombre_completo}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-[10px] font-black text-gray-500 uppercase mb-1">Fecha</label><input type="date" required min={alcanceABAC.esGlobal ? undefined : ayerStr} value={formExtra.fecha_inicio} onChange={(e) => setFormExtra({ ...formExtra, fecha_inicio: e.target.value })} className="w-full p-3 rounded-xl border border-gray-200 text-xs font-bold" /></div>
                <div><label className="block text-[10px] font-black text-amber-700 uppercase mb-1">Horas Extra</label><input type="number" step="0.5" min="0.5" max="12" required value={formExtra.horas_extra} onChange={(e) => setFormExtra({ ...formExtra, horas_extra: e.target.value })} className="w-full p-3 rounded-xl border border-amber-300 bg-amber-50/40 text-xs font-black text-amber-950" /></div>
              </div>
              <div><label className="block text-[10px] font-black text-gray-500 uppercase mb-1">Motivo</label><textarea rows="2" required value={formExtra.motivo} onChange={(e) => setFormExtra({ ...formExtra, motivo: e.target.value })} placeholder="Ej: Cierre..." className="w-full p-3 rounded-xl border border-gray-200 text-xs font-medium" /></div>
              <div className="flex justify-end gap-2 pt-3 border-t"><button type="button" onClick={() => setModalExtraAbierto(false)} className="px-4 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-bold text-xs cursor-pointer">Cancelar</button><button type="submit" disabled={guardandoExtra} className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-black text-xs shadow-md cursor-pointer">{guardandoExtra ? 'Enviando...' : 'Enviar Solicitud'}</button></div>
            </form>
          </div>
        </div>
      )}
      
      {diaMesEditando && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-lg w-full shadow-2xl border border-gray-100">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
              <div><h4 className="font-black text-slate-900 text-sm">Cobertura del {diaMesEditando.fechaStr}</h4></div>
              <button onClick={() => setDiaMesEditando(null)} className="w-8 h-8 rounded-full bg-gray-100 font-bold cursor-pointer">✕</button>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {empleadosSede.map(emp => {
                const tur = mapaTurnos.get(`${emp.id}_${diaMesEditando.fechaStr}`);
                return (
                  <div key={emp.id} onClick={() => { setDiaMesEditando(null); setCeldaEditando({ id_empleado: emp.id, nombre: emp.nombre_completo, fechaStr: diaMesEditando.fechaStr }); setEsCoberturaDoblaje(false); }} className="p-3 rounded-xl border border-gray-200 hover:border-blue-500 hover:bg-blue-50 flex justify-between items-center cursor-pointer transition">
                    <div><p className="text-xs font-black text-slate-900">{emp.nombre_completo}</p><span className="font-mono text-[10px] text-blue-700 font-bold">{emp.codigo_interno}</span></div>
                    <span className="px-2.5 py-1 rounded-lg bg-slate-100 font-bold text-[10px] text-slate-700">{tur ? tur.nombre_turno : 'DESCANSO'}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}